import type { SxProps } from '@mui/material';

/**
 * "Book a class" focus experiment.
 *
 * Target stage: a subscribed student opens their course page with no upcoming
 * class. Goal: make booking the next class the single obvious next step.
 *
 * The test variant restyles the no-upcoming-class prompt in the course hero — a
 * direct "No upcoming class / Book your next class now" message and a prominent,
 * animated (shimmer + glow) "Book a Class" button — against the control's plain
 * "Welcome to Tutopiya" copy and flat dark button. The same treatment is used
 * for the "Book a Lesson" CTA in the sticky bottom nav bar.
 *
 * Goal / final metric: `book_class_clicked` — the student presses "Book a Class"
 * from this prompt. Captured for every variant (the `variant` property tells
 * them apart) so conversion can be compared across the experiment.
 */

/** PostHog multivariate flag: 'control' | 'test'. */
export const BOOK_CLASS_FOCUS_FEATURE_FLAG = 'Class-booking-exp-new';

export const BOOK_CLASS_FOCUS_VARIANTS = {
  CONTROL: 'control',
  TEST: 'test',
} as const;

/**
 * Shimmer + glow + blue-to-indigo gradient treatment for the primary booking
 * CTA in the `book_class_focus` test variant. Spread over the button's own
 * sizing styles (padding, font size, border radius). The button must wrap its
 * label/icon in an element with `position: relative; zIndex: 1` so the shimmer
 * pseudo-element sits behind the text.
 */
export const BOOK_CLASS_FOCUS_CTA_SX: SxProps = {
  position: 'relative',
  overflow: 'hidden',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  background: 'linear-gradient(135deg, #3B82F6 0%, #4F46E5 100%)',
  color: '#FFFFFF',
  border: 'none',
  animation: 'bookGlow 3s ease-in-out infinite',
  '@keyframes bookGlow': {
    '0%, 100%': { boxShadow: '0 0 0 0 rgba(79, 70, 229, 0)' },
    '50%': { boxShadow: '0 0 20px 3px rgba(79, 70, 229, 0.5)' },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '-75%',
    width: '50%',
    background:
      'linear-gradient(120deg, transparent 0%, rgba(255, 255, 255, 0.55) 50%, transparent 100%)',
    transform: 'skewX(-20deg)',
    animation: 'bookShimmer 3s ease-in-out infinite',
  },
  '@keyframes bookShimmer': {
    '0%': { left: '-75%' },
    '55%, 100%': { left: '130%' },
  },
  '&:hover': {
    background: 'linear-gradient(135deg, #2563EB 0%, #4338CA 100%)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    '&::before': { display: 'none' },
  },
};
